import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Asset,
  AssetSource,
  MediaFormatFilter,
  MediaQuery,
  MediaType,
} from '@mg/shared';
import { MEDIA_BULK_MAX_ITEMS, MEDIA_PAGE_SIZE, MEDIA_TRASH_RETENTION_DAYS } from '@mg/shared';
import { Images, Sparkles, Upload } from 'lucide-react';
import { api, actionErrorText, ApiRequestError } from '../lib/api';
import { useTenantKey } from '../lib/useTenantKey';
import { PageHeader } from '../components/shell/PageHeader';
import { Button } from '../components/ui/Button';
import { EmptyState, ErrorState, LoadingState } from '../components/ui/States';
import { useDialogs } from '../components/ui/DialogProvider';
import {
  MediaFolderSidebar,
  type MediaView,
} from '../features/media/MediaFolderSidebar';
import { MediaToolbar } from '../features/media/MediaToolbar';
import { DesignToolImportModal } from '../features/media/DesignToolImportModal';
import { useDesignTools } from '../features/media/useDesignTools';
import { MediaBulkBar } from '../features/media/MediaBulkBar';
import { MediaCard, type MediaCardActions } from '../features/media/MediaCard';
import { Spinner } from '../components/ui/Spinner';
import { AssetViewerModal } from '../features/media/AssetViewerModal';
import { UploadMediaModal } from '../features/media/UploadMediaModal';
import { useMediaUploads } from '../features/media/useMediaUploads';
import { GenerateMediaModal } from '../features/media/GenerateMediaModal';
import { MoveToFolderModal } from '../features/media/MoveToFolderModal';
import { FORMAT_FILTER_NOTICE, downloadAssetsZip } from '../features/media/mediaDisplay';
import { useMediaPages } from '../features/media/useMediaPages';
import { MediaSearchBox } from '../features/media/MediaSearchBox';
import { LoadMoreButton } from '../features/media/LoadMoreButton';

type SourceFilter = AssetSource | 'all';
type TypeFilter = MediaType | MediaFormatFilter | 'all';

/** The trash's standing promise — the daily purge on the API enforces exactly this window. */
const TRASH_NOTICE = `Images in Trash for more than ${MEDIA_TRASH_RETENTION_DAYS} days will be automatically deleted.`;

/** Asset ids are UUIDs — `GET /assets/:id` refuses anything else (its `ParseUUIDPipe`). */
const ASSET_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Whether the answer to "this one asset" is that asset. The viewer draws an asset's fields as they
 * are — its type, its campaigns, its tags — so an answer of any other shape must not reach it.
 */
function isAssetAnswer(answer: unknown, id: string): answer is Asset {
  if (typeof answer !== 'object' || answer === null) return false;
  const asset = answer as Partial<Asset>;
  return (
    asset.id === id &&
    typeof asset.mimeType === 'string' &&
    Array.isArray(asset.campaigns) &&
    Array.isArray(asset.tags)
  );
}

/**
 * What to say before trashing files that posts still have to go out with, or null when no post
 * waits on any of them. A trashed file keeps publishing while it is in the trash; once it is
 * deleted for good, a post that held it is refused until another file is attached.
 *
 * One file names how many posts use it. Several files name how many of THEM are in use: the
 * per-file counts cannot be added up, because one post may hold two of the files. Several files
 * also say first how many are moving - every one of them goes, not only the ones in use, and this
 * warning stands where the usual question would have said so.
 */
function trashWarning(assets: Asset[]): string | null {
  const inUse = assets.filter((a) => (a.unpublishedPostCount ?? 0) > 0);
  if (inUse.length === 0) return null;
  const days = MEDIA_TRASH_RETENTION_DAYS;
  if (assets.length === 1) {
    const n = inUse[0].unpublishedPostCount ?? 0;
    return `This file is used by ${n} post${n === 1 ? '' : 's'} that ${n === 1 ? 'has' : 'have'} not been published yet. It stays usable for ${days} days in the trash. After that it is deleted for good, and ${n === 1 ? 'that post' : 'those posts'} will not publish until another file is attached.`;
  }
  const k = inUse.length;
  return `These ${assets.length} items move to the Trash, where you can restore them for ${days} days. ${k} of these files ${k === 1 ? 'is' : 'are'} used by posts that have not been published yet. ${k === 1 ? 'It stays' : 'They stay'} usable for ${days} days in the trash. After that ${k === 1 ? 'it is' : 'they are'} deleted for good, and those posts will not publish until another file is attached.`;
}

/**
 * Media Library — the permanent home for uploaded + AI-generated brand media.
 * A folder rail on the left slices the grid (All / a folder / Trash); toolbar chips
 * filter by source and media type, and the search box by name, prompt and tag. Per-asset actions
 * live on each card, bulk actions on the selection bar. The grid arrives a page at a time ("Load
 * more"); it and the folder counts share the `['media', tk, ...]` / `['asset-folders', tk]` caches.
 */
export function MediaLibraryPage() {
  const tenantKey = useTenantKey();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { alert, confirm } = useDialogs();
  const { tool: designTool, label: designToolName } = useDesignTools();

  const [view, setView] = useState<MediaView>({ kind: 'folder', folderId: null });
  const [source, setSource] = useState<SourceFilter>('all');
  const [type, setType] = useState<TypeFilter>('all');
  /** The search in force, trimmed. It narrows the open folder ("All media" searches everything). */
  const [search, setSearch] = useState('');
  /** Set only for the two editing formats — the ones that cannot be posted. */
  const formatNotice = FORMAT_FILTER_NOTICE[type as MediaFormatFilter];
  const [viewerAsset, setViewerAsset] = useState<Asset | null>(null);
  /** Consumed once, so closing the viewer doesn't immediately reopen it from the same URL. */
  const openedFromUrl = useRef(false);
  const [moveAssets, setMoveAssets] = useState<Asset[]>([]);
  // Which surface opened the Move modal. Inferring it from the payload is ambiguous — a one-item
  // bulk move and a kebab move of an already-selected asset look identical — and only the bulk
  // one should clear the selection afterwards.
  const [movingSelection, setMovingSelection] = useState(false);
  const [generating, setGenerating] = useState(false);
  /** Canva/Figma import modal — also opened by the Generate modal's Generator dropdown. */
  const [importOpen, setImportOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [uploadOpen, setUploadOpen] = useState(false);

  const isTrash = view.kind === 'trash';
  const activeFolderId = view.kind === 'folder' ? view.folderId : null;

  const query = useMemo<MediaQuery>(
    () => ({
      source,
      type,
      deleted: isTrash,
      folderId: view.kind === 'folder' && view.folderId ? view.folderId : undefined,
      ...(search ? { q: search } : {}),
    }),
    [source, type, isTrash, view, search],
  );

  const foldersQuery = useQuery({
    queryKey: ['asset-folders', tenantKey],
    queryFn: ({ signal }) => api.listAssetFolders(signal),
    enabled: tenantKey !== '',
  });
  const folders = useMemo(() => foldersQuery.data ?? [], [foldersQuery.data]);

  // A page at a time. Polls while anything loaded is still generating, so finished results swap in.
  const media = useMediaPages(query, MEDIA_PAGE_SIZE);
  const assets = media.assets;

  /** False once the page is gone, so a late answer for `?asset=` neither opens a viewer nor navigates. */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Open a specific asset on arrival, for "View in Media Library" in the Generate modal.
   *
   * Looks in the first loaded page, and fetches the asset on its own when it is not there: the list
   * arrives a page at a time now, so a file further down would otherwise never open. Runs ONCE per
   * navigation — without the ref, closing the viewer would leave `?asset=` in the URL and the effect
   * would reopen it, making the modal unclosable. The param is stripped as soon as it is used, so a
   * refresh doesn't resurrect it either; a file that is gone just strips it.
   *
   * Only an asset id is followed. The value comes from the address bar and goes into a request's
   * path, where a `..` or a `?` would send the signed-in browser to any other route of the API —
   * so anything else is treated as no link at all.
   */
  useEffect(() => {
    const wanted = params.get('asset');
    if (!wanted || !ASSET_ID.test(wanted) || openedFromUrl.current || media.isPending) return;
    openedFromUrl.current = true;
    // From the URL as it is when the answer lands: the user may have moved on in the meantime.
    const stripParam = () =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete('asset');
          return next;
        },
        { replace: true },
      );
    const found = assets.find((a) => a.id === wanted);
    if (found) {
      setViewerAsset(found);
      stripParam();
      return;
    }
    api.getAsset(wanted).then(
      (asset) => {
        if (!mounted.current) return;
        // An answer that is not this asset is a dead link too.
        if (isAssetAnswer(asset, wanted)) setViewerAsset(asset);
        stripParam();
      },
      () => {
        if (mounted.current) stripParam();
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, assets, media.isPending]);

  // Bulk selection is offered on the library views only. The trash's actions are Restore and a
  // 30-day auto-purge, neither of which any of the bulk buttons mean.
  const selectable = !isTrash;

  // Resolve against what is actually on screen: filters, folder switches and refetches all change
  // the grid under the selection, and a bulk action must never touch a tile the user can't see.
  const selectedAssets = useMemo(
    () => assets.filter((a) => selectedIds.has(a.id)),
    [assets, selectedIds],
  );
  const clearSelection = () => setSelectedIds(new Set());
  /** What is ticked NOW, for an action that has to wait for a read before it can ask. */
  const selectedIdsNow = useRef(selectedIds);
  selectedIdsNow.current = selectedIds;

  // Drop the selection whenever the visible set changes out from under it — switching folders or
  // filters is a change of context, and carrying a hidden selection across is how people delete
  // things they never saw.
  //
  // Keyed on a STRING, not on `query`'s object identity: the folder rail hands back a fresh view
  // object on every click, so re-clicking the folder you are already in produced a new identity,
  // no refetch, an unchanged grid — and a silently wiped selection.
  const queryKey = JSON.stringify(query);
  useEffect(() => {
    setSelectedIds(new Set());
  }, [queryKey, tenantKey]);

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['media', tenantKey] });
    void queryClient.invalidateQueries({ queryKey: ['asset-folders', tenantKey] });
  };

  // Owned here, not by the modal: closing the dialog must not cancel a 90 MB video, so the queue
  // has to outlive it. The Upload button reads the same state to show what is still running.
  const uploads = useMediaUploads({ folderId: activeFolderId, onUploaded: invalidateAll });


  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteAsset(id),
    onSuccess: invalidateAll,
    onError: (err) =>
      void alert({
        title: 'Something went wrong',
        message: actionErrorText(err, 'Could not delete this asset.'),
      }),
  });

  const restoreMut = useMutation({
    mutationFn: (id: string) => api.restoreAsset(id),
    onSuccess: invalidateAll,
    onError: (err) =>
      void alert({
        title: 'Something went wrong',
        message: actionErrorText(err, 'Could not restore this asset.'),
      }),
  });

  const bulkDeleteMut = useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteAssets(ids),
    onSuccess: () => {
      clearSelection();
      invalidateAll();
    },
    onError: (err) =>
      void alert({
        title: 'Something went wrong',
        message: actionErrorText(err, 'Could not delete the selected media.'),
      }),
  });

  const bulkDownloadMut = useMutation({
    mutationFn: (ids: string[]) => downloadAssetsZip(ids),
    onError: (err) =>
      void alert({
        title: 'Something went wrong',
        message: actionErrorText(err, 'Could not build the download.'),
      }),
  });

  // The API caps a bulk request at MEDIA_BULK_MAX_ITEMS, so the grid has to cap the selection
  // too — otherwise the 201st tick looks fine and every action then fails with a raw validator
  // message. Refusing the tick and saying why beats letting them build a selection that can't run.
  const toggleSelect = (asset: Asset) => {
    // The cap is checked HERE, not inside the updater: a setState updater must be pure, and React
    // double-invokes it under StrictMode — so an alert in there fired twice per refused click.
    if (!selectedIds.has(asset.id) && selectedIds.size >= MEDIA_BULK_MAX_ITEMS) {
      void alert({
        title: 'Selection limit reached',
        message: `You can act on up to ${MEDIA_BULK_MAX_ITEMS} items at a time. Deselect something first, or handle them in batches.`,
      });
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(asset.id)) next.delete(asset.id);
      else next.add(asset.id);
      return next;
    });
  };

  /** Select as much of the current grid as one bulk request can carry. */
  const selectAll = () =>
    setSelectedIds(new Set(assets.slice(0, MEDIA_BULK_MAX_ITEMS).map((a) => a.id)));

  /**
   * One file as the server has it now. The grid's count of the posts waiting on a file is as old as
   * the grid's last read, and a post may have taken the file since - so a delete asks about the
   * file as it is. When it cannot be read, the row on screen stands in: a failed read neither holds
   * the delete up nor drops a warning the grid already had reason for.
   */
  const readAgain = (asset: Asset): Promise<Asset> =>
    api.getAsset(asset.id).then(
      (fresh) => (isAssetAnswer(fresh, asset.id) ? fresh : asset),
      () => asset,
    );

  // One file from its card. Asked only when a post still has to go out with it; a file no
  // unpublished post uses goes to the trash at once, as it always did.
  const askDelete = async (asset: Asset) => {
    const warning = trashWarning([await readAgain(asset)]);
    if (
      warning &&
      // The page may have been left while the file was read: no question is put over another page.
      (!mounted.current ||
        !(await confirm({
          title: 'Move to trash?',
          message: warning,
          confirmLabel: 'Move to trash',
          danger: true,
        })))
    ) {
      return;
    }
    deleteMut.mutate(asset.id);
  };

  /** True from the bulk bar's Delete being pressed until its question is answered: one at a time. */
  const askingBulkDelete = useRef(false);
  const askBulkDelete = async () => {
    if (askingBulkDelete.current || selectedAssets.length === 0) return;
    askingBulkDelete.current = true;
    try {
      // Several files: the list is read again and the question is about the rows it holds now -
      // of the files ticked at that moment, the ones still listed. A read that fails leaves the
      // rows as they were, so the grid's own counts decide and the delete is not held up.
      const reread = await media.refetch();
      if (!mounted.current) return;
      const listed = new Map<string, Asset>();
      for (const page of reread.data?.pages ?? []) {
        for (const row of page.items) if (selectedIdsNow.current.has(row.id)) listed.set(row.id, row);
      }
      // A ticked file the read did not reach (a newer upload pushed it onto a page not loaded yet)
      // is still there: it keeps the row the grid shows. Only a read that reached the end of the
      // list can tell that a ticked file is gone.
      if (reread.data?.pages.at(-1)?.nextCursor) {
        for (const a of selectedAssets) {
          if (selectedIdsNow.current.has(a.id) && !listed.has(a.id)) listed.set(a.id, a);
        }
      }
      const selected = [...listed.values()];
      const n = selected.length;
      if (n === 0) return;
      // The same warning takes the place of the usual question when a post waits on any of them.
      const warning = trashWarning(selected);
      if (
        await confirm(
          warning
            ? { title: 'Move to trash?', message: warning, confirmLabel: 'Move to trash', danger: true }
            : {
                title: n === 1 ? 'Delete this item' : `Delete ${n} items`,
                message: `${n === 1 ? 'This item moves' : `These ${n} items move`} to the Trash, where you can restore ${n === 1 ? 'it' : 'them'} for ${MEDIA_TRASH_RETENTION_DAYS} days before ${n === 1 ? 'it is' : 'they are'} deleted automatically.`,
                confirmLabel: 'Move to Trash',
                danger: true,
              },
        )
      ) {
        bulkDeleteMut.mutate(selected.map((a) => a.id));
      }
    } finally {
      askingBulkDelete.current = false;
    }
  };

  const openMove = (assets: Asset[], fromSelection: boolean) => {
    setMovingSelection(fromSelection);
    setMoveAssets(assets);
  };

  const cardActions: MediaCardActions = {
    onOpen: setViewerAsset,
    onMove: (a) => openMove([a], false),
    onDelete: (a) => void askDelete(a),
    onRestore: (a) => restoreMut.mutate(a.id),
    onToggleSelect: selectable ? toggleSelect : undefined,
  };

  const currentFolderName =
    view.kind === 'folder' && view.folderId
      ? (folders.find((f) => f.id === view.folderId)?.name ?? 'Folder')
      : null;
  const heading = isTrash ? 'Trash' : (currentFolderName ?? 'All media');
  const bulkBusy = bulkDeleteMut.isPending || bulkDownloadMut.isPending;

  return (
    <>
      <PageHeader
        title="Media Library"
        icon={Images}
        description="Organize your uploaded and AI-generated brand media."
        actions={
          !isTrash && (
            <>
              <Button variant="secondary" onClick={() => setUploadOpen(true)}>
                <Upload className="h-4 w-4" aria-hidden />
                Upload
                {/* Uploads survive closing the dialog, so without this the only evidence of a
                    running upload would be the file appearing later, on its own. */}
                {uploads.uploading > 0 && (
                  <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-tint px-1.5 py-0.5 text-[0.6875rem] font-semibold text-tint-fg">
                    <Spinner className="h-3 w-3" />
                    {uploads.uploading}
                  </span>
                )}
              </Button>
              {/* Between Upload and Generate new — it is the same act (get artwork into the
                  library) sourced from a design tool instead of a file or a prompt. */}
              {designTool && (
                <Button variant="secondary" onClick={() => setImportOpen(true)}>
                  <Images className="h-4 w-4" aria-hidden />
                  Import from {designToolName}
                </Button>
              )}
              <Button onClick={() => setGenerating(true)}>
                <Sparkles className="h-4 w-4" aria-hidden />
                Generate new
              </Button>
            </>
          )
        }
      />

      <div className="flex flex-col gap-6 md:flex-row">
        <aside className="md:w-56 md:shrink-0">
          <MediaFolderSidebar
            folders={folders}
            view={view}
            onSelect={setView}
            tenantKey={tenantKey}
          />
        </aside>

        <section className="min-w-0 flex-1 space-y-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            {/* flex-1 + min-w-0: the chips wrap inside their own box, so the search box stays on this
                row beside them instead of being the item the row pushes onto a line of its own. */}
            <div className="min-w-0 flex-1">
              <MediaToolbar source={source} onSource={setSource} type={type} onType={setType} />
            </div>
            <MediaSearchBox value={search} onSearch={setSearch} className="w-full sm:w-64" />
          </div>

          {/* Shown only while the format's own filter is active: it explains that view, and on every
              other view it would be noise about files the user is not looking at. */}
          {formatNotice && (
            <div className="rounded-xl border border-line bg-page p-3 text-sm text-ink-label">
              <p>{formatNotice.body}</p>
              <p className="mt-2 text-ink-strong">{formatNotice.caution}</p>
            </div>
          )}

          {selectedAssets.length > 0 && (
            <MediaBulkBar
              count={selectedAssets.length}
              selectableTotal={Math.min(assets.length, MEDIA_BULK_MAX_ITEMS)}
              busy={bulkBusy}
              downloading={bulkDownloadMut.isPending}
              onSelectAll={selectAll}
              onClear={clearSelection}
              onMove={() => openMove(selectedAssets, true)}
              onDownload={() => bulkDownloadMut.mutate(selectedAssets.map((a) => a.id))}
              onDelete={() => void askBulkDelete()}
            />
          )}

          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-strong">{heading}</h2>
            {!media.isLoading && !media.isError && (
              <span className="text-xs text-ink-faint">
                {media.total} item{media.total === 1 ? '' : 's'}
              </span>
            )}
          </div>

          {isTrash && <p className="-mt-2 text-sm text-ink-muted">{TRASH_NOTICE}</p>}

          {media.isLoading ? (
            <LoadingState label="Loading media…" />
          ) : media.isError ? (
            <ErrorState
              message={
                media.error instanceof ApiRequestError
                  ? media.error.message
                  : 'Could not load your media.'
              }
              onRetry={() => void media.refetch()}
            />
          ) : assets.length === 0 && search ? (
            <EmptyState
              title="No media matches your search"
              description="Try a different word, or clear the search."
            />
          ) : assets.length === 0 ? (
            <EmptyState
              title={
                isTrash
                  ? 'Trash is empty'
                  : currentFolderName
                    ? 'This folder is empty'
                    : 'No media yet'
              }
              description={
                isTrash
                  ? 'Deleted media appears here and can be restored.'
                  : currentFolderName
                    ? 'Upload media into this folder, or move existing items here.'
                    : 'Upload images, video, or audio — or generate new media — to build your library.'
              }
              action={
                !isTrash && (
                  <Button onClick={() => setUploadOpen(true)}>
                    <Upload className="h-4 w-4" aria-hidden />
                    Upload media
                  </Button>
                )
              }
            />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {assets.map((asset) => (
                  <MediaCard
                    key={asset.id}
                    asset={asset}
                    isTrash={isTrash}
                    selected={selectedIds.has(asset.id)}
                    anySelected={selectedAssets.length > 0}
                    actions={cardActions}
                  />
                ))}
              </div>
              {/* A growing list rather than numbered pages: ticked tiles stay ticked while more load. */}
              {media.hasMore && (
                <div className="flex flex-col items-center gap-2 pt-2">
                  <p className="text-xs text-ink-muted">
                    Showing {assets.length} of {media.total}
                  </p>
                  <LoadMoreButton
                    loading={media.isFetchingNextPage}
                    failed={media.loadMoreFailed}
                    onClick={media.loadMore}
                  />
                </div>
              )}
            </>
          )}
        </section>
      </div>

      <AssetViewerModal asset={viewerAsset} onClose={() => setViewerAsset(null)} />

      <UploadMediaModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        items={uploads.items}
        onAdd={uploads.add}
        onCancelInFlight={uploads.cancelInFlight}
        uploading={uploads.uploading}
      />
      <MoveToFolderModal
        assets={moveAssets}
        folders={folders}
        onClose={() => setMoveAssets([])}
        // Only clear when the move acted on the SELECTION. Moving one asset from its kebab menu
        // must not throw away a bulk selection the user is still building.
        onMoved={movingSelection ? clearSelection : undefined}
        tenantKey={tenantKey}
      />
      {designTool && (
        <DesignToolImportModal
          tool={designTool}
          open={importOpen}
          folderId={activeFolderId ?? undefined}
          onClose={() => setImportOpen(false)}
        />
      )}
      <GenerateMediaModal
        open={generating}
        onClose={() => setGenerating(false)}
        // Selecting Canva/Figma in the Generator dropdown is an IMPORT, not a generation —
        // it routes here rather than being forced through the prompt->image contract.
        onUseDesignTool={() => {
          setGenerating(false);
          setImportOpen(true);
        }}
        folderId={activeFolderId ?? undefined}
        tenantKey={tenantKey}
      />
    </>
  );
}
